import { getAuth, GoogleAuthProvider, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { sessionMarker, sessionChangedError } from './BrowserSessionService';
import { safeAuthRedirect, rememberInvitation } from '../utils/OnboardingRedirect';

const KEY = 'ledgerloop.google-redirect';
const MAX_AGE = 30 * 60 * 1000;

export function usesGoogleRedirect() {
  // The Firebase SDK always loads its authDomain helpers over HTTPS.
  return window.location.protocol === 'https:';
}

export function googleAuthDomain() {
  return usesGoogleRedirect() ? window.location.host : process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
}

export function clearGoogleRedirect() {
  try { sessionStorage.removeItem(KEY); } catch { /* Storage may be disabled. */ }
}

export async function beginGoogleRedirect(destination) {
  const redirect = safeAuthRedirect(destination);
  rememberInvitation(redirect);
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ redirect, marker: sessionMarker(), startedAt: Date.now() }));
  } catch {
    throw Object.assign(new Error('Browser storage unavailable'), { code: 'auth/web-storage-unsupported' });
  }
  // Start only from the user's click, never automatically retry navigation.
  // Back may restore this page from bfcache with the original navigation
  // promise still pending. Boot again so it can report cancellation normally.
  const onPageShow = event => {
    if (event.persisted) window.location.reload();
  };
  window.addEventListener('pageshow', onPageShow);
  try {
    await signInWithRedirect(getAuth(), new GoogleAuthProvider());
  } finally {
    window.removeEventListener('pageshow', onPageShow);
  }
}

export async function takeGoogleRedirect() {
  let raw;
  try { raw = sessionStorage.getItem(KEY); }
  catch { return null; }
  if (!raw) return null;
  // Consume once, including Back/cancellation and failure. No ID tokens or
  // credentials are stored in our redirect metadata.
  clearGoogleRedirect();
  let pending;
  try { pending = JSON.parse(raw); } catch { /* Reject malformed metadata. */ }
  if (!pending || !Number.isFinite(pending.startedAt) || Date.now() < pending.startedAt ||
      Date.now() - pending.startedAt > MAX_AGE) {
    throw Object.assign(new Error('Expired login'), { code: 'auth/redirect-expired' });
  }
  try {
    const result = await getRedirectResult(getAuth());
    if (pending.marker !== sessionMarker()) throw sessionChangedError();
    if (!result) throw Object.assign(new Error('No sign-in result'), { code: 'auth/redirect-cancelled' });
    return { result, redirect: safeAuthRedirect(pending.redirect), marker: pending.marker };
  } catch (error) {
    error.redirect = safeAuthRedirect(pending.redirect);
    throw error;
  }
}
